import type { FoxAttack } from '../../../data/full/enracine';
import { angleOf } from '../../../shared/angle';
import { distanceSquared } from '../../../shared/vec';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { hurtEnemy } from '../../effects';
import type { EnemyState, PlayerState, PropState, SimState, Vec2 } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { rootNotches } from '../gauges/racines';
import { closeIn, flyBack, ownerOf } from '../../props';

export const FOX = 'fox';
const HOP_TICKS = 6;

export const fox: AttackModule<FoxAttack> = {
  reads: ['attackCooldownMul', 'damageMul', 'pierceAdd'],

  ammunition: true,

  step(ctx, player, attack, input) {
    const { state } = ctx;
    const owned = foxOf(state, player);
    const atFeet = owned === undefined || owned.phase === 'held';
    const prey = atFeet ? firstPrey(state.enemies, player, attack.reach) : undefined;
    if (!pullTrigger(player, input.fire && prey !== undefined, attack.cooldownTicks)) {
      return;
    }
    if (prey === undefined) {
      return;
    }
    const leaping = owned ?? newFox(state, player);
    const notches = rootNotches(player);
    leaping.phase = 'bouncing';
    leaping.x = leaping.prevX = player.x;
    leaping.y = leaping.prevY = player.y;
    leaping.targetId = prey.id;
    leaping.ticksLeft = leapTicks(attack.reach, attack.speed);
    leaping.damage =
      statValue(player, 'damageMul', attack.damage) * (1 + attack.roots.damage * notches);
    leaping.hitsLeft = statValue(player, 'pierceAdd', attack.leaps) + attack.roots.leaps * notches;
    leaping.hitIds = [];
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  update(ctx) {
    const { state } = ctx;
    for (const prop of state.props ?? []) {
      if (prop.kind !== FOX) {
        continue;
      }
      const owner = ownerOf(state, prop);
      const attack = attackOf(ctx, owner);
      if (prop.phase === 'bouncing') {
        leap(ctx, prop, attack);
      } else if (prop.phase === 'flying') {
        hop(ctx, prop, attack);
      } else if (prop.phase === 'returning') {
        flyBack(prop, owner, attack.returnSpeed);
      }
    }
  },

  reach(attack) {
    return attack.reach;
  },
};

export function foxOf(state: SimState, player: Pick<PlayerState, 'id'>): PropState | undefined {
  return state.props?.find((prop) => prop.kind === FOX && prop.playerId === player.id);
}

function firstPrey(
  enemies: readonly EnemyState[],
  player: PlayerState,
  reach: number,
): EnemyState | undefined {
  let ahead: EnemyState | undefined;
  let any: EnemyState | undefined;
  for (const enemy of enemies) {
    const away = reach + enemy.radius;
    const distance = distanceSquared(enemy, player);
    if (enemy.hp <= 0 || distance > away * away) {
      continue;
    }
    if (any === undefined || distance < distanceSquared(any, player)) {
      any = enemy;
    }
    const along = (enemy.x - player.x) * player.aim.x + (enemy.y - player.y) * player.aim.y;
    if (along >= 0 && (ahead === undefined || distance < distanceSquared(ahead, player))) {
      ahead = enemy;
    }
  }
  return ahead ?? any;
}

function newFox(state: SimState, player: PlayerState): PropState {
  const made: PropState = {
    id: state.nextEntityId,
    kind: FOX,
    playerId: player.id,
    phase: 'held',
    x: player.x,
    y: player.y,
    prevX: player.x,
    prevY: player.y,
    vx: 0,
    vy: 0,
    damage: 0,
    ticksLeft: 0,
    hitsLeft: 0,
  };
  state.nextEntityId += 1;
  (state.props ??= []).push(made);
  return made;
}

function attackOf(ctx: StepContext, owner: PlayerState): FoxAttack {
  const attack = ctx.content.classes.get(owner.classId)?.attack;
  if (attack?.kind !== 'fox') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no fox`);
  }
  return attack;
}

// Twice the leap: a bad vibe running away never holds the fox long.
function leapTicks(distance: number, speed: number): number {
  return Math.ceil((2 * distance) / speed);
}

function leap(ctx: StepContext, prop: PropState, attack: FoxAttack): void {
  const { state } = ctx;
  let target = state.enemies.find((enemy) => enemy.id === prop.targetId && enemy.hp > 0);
  if (target === undefined) {
    target = nearestUnhit(state, prop, attack.range);
    if (target !== undefined) {
      prop.targetId = target.id;
    }
  }
  prop.ticksLeft -= 1;
  if (target === undefined || prop.ticksLeft <= 0) {
    runHome(prop);
    return;
  }
  const from: Vec2 = { x: prop.x, y: prop.y };
  if (closeIn(prop, target, target.radius, attack.speed)) {
    bite(ctx, prop, attack, target, from);
  }
}

function bite(
  ctx: StepContext,
  prop: PropState,
  attack: FoxAttack,
  enemy: EnemyState,
  from: Vec2,
): void {
  const { state } = ctx;
  hurtEnemy(state, enemy, prop.damage, prop.playerId, from);
  (prop.hitIds ??= []).push(enemy.id);
  prop.hitsLeft -= 1;
  prop.damage *= attack.falloff;
  if (prop.hitsLeft <= 0) {
    runHome(prop);
    return;
  }
  const next = nearestUnhit(state, prop, attack.range);
  if (next === undefined) {
    if (enemy.hp <= 0) {
      runHome(prop);
      return;
    }
    prop.phase = 'flying';
    prop.ticksLeft = HOP_TICKS;
    return;
  }
  prop.targetId = next.id;
  prop.ticksLeft = leapTicks(attack.range, attack.speed);
}

function nearestUnhit(state: SimState, prop: PropState, range: number): EnemyState | undefined {
  let nearest: EnemyState | undefined;
  let best = range * range;
  for (const enemy of state.enemies) {
    if (enemy.hp <= 0 || prop.hitIds?.includes(enemy.id) === true) {
      continue;
    }
    const distance = distanceSquared(enemy, prop);
    if (distance <= best) {
      nearest = enemy;
      best = distance;
    }
  }
  return nearest;
}

// Alone in its range, the bad vibe it just bit takes the next leap too: the fox hops on it.
function hop(ctx: StepContext, prop: PropState, attack: FoxAttack): void {
  const prey = ctx.state.enemies.find((enemy) => enemy.id === prop.targetId && enemy.hp > 0);
  if (prey === undefined) {
    runHome(prop);
    return;
  }
  const from: Vec2 = { x: prop.x, y: prop.y };
  prop.x = prey.x;
  prop.y = prey.y;
  prop.ticksLeft -= 1;
  if (prop.ticksLeft <= 0) {
    prop.phase = 'bouncing';
    bite(ctx, prop, attack, prey, from);
  }
}

function runHome(prop: PropState): void {
  prop.phase = 'returning';
  delete prop.targetId;
  delete prop.hitIds;
}
